import * as React from 'react';
import Flex from 'ui/Flex/Flex';
import { useGetAvailableDimensions } from 'routes/Explore/components/Sentence/hooks/available';
import { Autocomplete, capitalize, TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import { translateBlock } from 'i18n';
import { useTranslation } from 'react-i18next';
import PreviousStepButton from './PreviousStepButton';
import NextStepButton from './NextStepButton';
import { useAlertForm } from '../Contexts/AlertFormContext';
import { useAlertSentence } from '../Contexts/AlertSentenceContext';
import { useAlertAction } from '../Contexts/AlertActionContext';

const AlertVentilation = (): JSX.Element => {
    const { alert, setAlert } = useAlertForm();
    const { action } = useAlertAction();
    const { availableDimensions, setAvailableDimensions } = useAlertSentence();
    const { data, isLoading, isFetching } = useGetAvailableDimensions(
        alert.metric ? [alert.metric] : [],
        !availableDimensions,
    );
    const { t } = useTranslation();

    const [dimensionSelected, setDimensionSelected] =
        useState<AvailableDimensionT | null>(null);

    const onDimensionChange = (
        newDimension: AvailableDimensionT | null,
    ): void => {
        setDimensionSelected(newDimension);
        setAlert((prevState) => {
            if (newDimension === null) {
                const { ...rest } = prevState;
                delete rest.dimension;
                return rest;
            }

            return {
                ...prevState,
                dimension: {
                    field: newDimension.field,
                },
            };
        });
    };

    useEffect(() => {
        if (data && !isLoading && !isFetching) {
            const categoryAlertDimensions = data.filter(
                (a) => a.type === 'category',
            );
            const sortedAlertDimensions = categoryAlertDimensions.sort((a, b) =>
                translateBlock(a.table).localeCompare(translateBlock(b.table)),
            );
            setAvailableDimensions(sortedAlertDimensions);

            const defaultDimension = sortedAlertDimensions.find(
                (m) => m.field === alert.dimension?.field,
            );

            if (defaultDimension) {
                setDimensionSelected(defaultDimension);
            }
        }
    }, [
        data,
        isLoading,
        isFetching,
        alert.dimension?.field,
        setAvailableDimensions,
    ]);

    return (
        <Flex direction="column">
            <Autocomplete
                options={availableDimensions ?? []}
                value={dimensionSelected}
                disabled={isLoading || isFetching}
                loading={isLoading || isFetching}
                onChange={(e, value) => onDimensionChange(value)}
                groupBy={(option) => capitalize(translateBlock(option.table))}
                getOptionLabel={(option) =>
                    capitalize(translateBlock(option.field))
                }
                sx={{ flex: 1, mt: 1 }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        size="small"
                        label={
                            isLoading || isFetching
                                ? t('common.loading')
                                : t('alert.create.ventilatedBy')
                        }
                    />
                )}
            />
            {action.name === 'create' && (
                <Flex align="center" sx={{ gap: 2 }}>
                    <PreviousStepButton />
                    <NextStepButton className="pendo-alerts-ventilation-step" />
                </Flex>
            )}
        </Flex>
    );
};

export default AlertVentilation;
